/** Booking touchpoints — VisitAllNepal books; operators are assigned internally. */

import { isGroupBookingEnabled, isPrivateBookingEnabled } from '../utils/journeyBookingFlags';

export const BOOKING_ROUTES = {
	hub: '/book-experiences',
	flights: '/flights',
	group: '/group-trips',
	private: '/packages',
	shared: '/shared-rentals',
};

/** Primary message for overseas travelers — reuse on home, flights, and booking hub. */
export const INTERNATIONAL_CHECKOUT = {
	eyebrow: 'Visiting from outside Nepal?',
	headlineBefore: 'Book with your',
	headlineStrong: 'Visa, Mastercard, or Amex',
	sub: 'Domestic flights, group treks, homestays & tours — secure checkout in USD or NPR. No Nepali wallet required.',
	scopeLabels: ['Domestic flights', 'Group trekking', 'Homestays & tours'],
	cardBrands: ['Visa', 'Mastercard', 'Amex'],
};

export const BOOKING_TRUST_POINTS = [
	'Visa, Mastercard & international cards — pay in USD or NPR',
	'Secure checkout through VisitAllNepal',
	'Small booking deposit',
	'Verified local operators',
	'Local support before and during your trip',
	'We assign the best available operator for your trip',
];

export const HOME_FLIGHTS_EXPERIENCE_CARD = {
	id: 'flights',
	emoji: '✈️',
	badge: 'Visa · Mastercard',
	title: 'Domestic flights in Nepal',
	lead: 'Kathmandu, Pokhara, Lukla, Bharatpur & more — pay with your foreign card at checkout (USD or NPR).',
	steps: ['Search route & date', 'Add passengers', 'Pay with Visa, MC or Amex'],
	cta: 'Search flights',
	route: BOOKING_ROUTES.flights,
	primary: true,
};

export const HOME_EXPERIENCE_CARDS = [
	HOME_FLIGHTS_EXPERIENCE_CARD,
	{
		id: 'group',
		emoji: '🏔',
		badge: 'Most popular',
		title: 'Join a Group Departure',
		lead: 'Fixed dates, shared costs, solo-friendly. Reserve with your Visa or Mastercard — small deposit online.',
		steps: ['Choose a departure date', 'Pay deposit online', 'We assign your operator'],
		cta: 'Book group departure',
		route: BOOKING_ROUTES.group,
	},
	{
		id: 'private',
		emoji: '🎒',
		badge: 'Flexible dates',
		title: 'Travel on Your Own Dates',
		lead: 'Private itinerary on your dates — homestays, culture & trekking. Pay deposit with your international card.',
		steps: ['Share your dates & route', 'Get a quote from us', 'Confirm & travel'],
		cta: 'Request private tour',
		route: BOOKING_ROUTES.private,
	},
	{
		id: 'shared',
		emoji: '🚐',
		badge: 'Budget transport',
		title: 'Shared Intercity Rides',
		lead: 'Kathmandu, Pokhara, Chitwan & more. Reserve your seat — pay with your international card.',
		steps: ['Pick route & date', 'Reserve your seat', 'Travel with others'],
		cta: 'Book shared ride',
		route: BOOKING_ROUTES.shared,
	},
];

const JOURNEY_TREK_OPTIONS = [
	{
		id: 'group',
		emoji: '🏔',
		title: 'Join a Group Departure',
		bestFor: 'Solo travelers, friends, budget travelers',
		cta: 'View Available Departures',
		route: BOOKING_ROUTES.group,
	},
	{
		id: 'private',
		emoji: '🎒',
		title: 'Plan a Private Tour',
		bestFor: 'Choose your own dates. Travel at your own pace.',
		cta: 'Request Private Tour',
		route: BOOKING_ROUTES.private,
	},
	{
		id: 'shared',
		emoji: '🚐',
		title: 'Arrange Shared Transportation',
		bestFor: 'Need transport to the trailhead or between cities?',
		cta: 'Browse Shared Rides',
		route: BOOKING_ROUTES.shared,
	},
];

const JOURNEY_HERITAGE_OPTIONS = [
	{
		id: 'private',
		emoji: '🎒',
		title: 'Plan a Private Tour',
		bestFor: 'Your dates, your pace — cultural guide included',
		cta: 'Request Private Tour',
		route: BOOKING_ROUTES.private,
	},
	{
		id: 'shared',
		emoji: '🚐',
		title: 'Arrange Shared Transportation',
		bestFor: 'Getting to Janakpur, Lumbini, or Terai towns',
		cta: 'Browse Shared Rides',
		route: BOOKING_ROUTES.shared,
	},
];

const JOURNEY_SAFARI_OPTIONS = [
	{
		id: 'private',
		emoji: '🎒',
		title: 'Plan a Safari Package',
		bestFor: 'Private jungle safari on your preferred dates',
		cta: 'Request Private Tour',
		route: BOOKING_ROUTES.private,
	},
	{
		id: 'shared',
		emoji: '🚐',
		title: 'Arrange Shared Transportation',
		bestFor: 'Kathmandu or Pokhara to Chitwan',
		cta: 'Browse Shared Rides',
		route: BOOKING_ROUTES.shared,
	},
];

/** Personalized end-of-journey copy — shown only at the bottom of long journey pages. */
export function journeyEndBookingHeadline(journey) {
	if (!journey) {
		return { lead: "You've finished planning your Nepal adventure.", question: 'Ready to make it happen?' };
	}

	const slug = journey.slug || '';
	const shortTitle = (journey.title || '').replace(/\s+Journey$/i, '').trim();

	if (slug === 'everest-base-camp') {
		return {
			lead: "You've finished planning your Everest adventure.",
			question: 'Ready to make it happen?',
		};
	}

	if (slug === 'annapurna-base-camp') {
		return {
			lead: "You've finished planning your Annapurna Base Camp adventure.",
			question: 'Ready to make it happen?',
		};
	}

	if (slug === 'langtang-valley') {
		return {
			lead: "You've finished planning your Langtang adventure.",
			question: 'Ready to make it happen?',
		};
	}

	const region = journey.region || '';
	if (region.includes('Wildlife') || region.includes('lowland')) {
		return {
			lead: `You've finished planning your ${shortTitle || 'safari'} itinerary.`,
			question: 'Ready to make it happen?',
		};
	}

	if (region.includes('Pilgrimage') || region.includes('culture')) {
		return {
			lead: `You've finished planning your ${shortTitle || 'cultural'} journey.`,
			question: 'Ready to make it happen?',
		};
	}

	const name = shortTitle || 'trek';
	return {
		lead: `You've finished planning your ${name} adventure.`,
		question: 'Ready to make it happen?',
	};
}

export function bookingOptionsForJourney(journey) {
	let options;
	if (!journey) options = JOURNEY_TREK_OPTIONS;
	else {
		const region = journey.region || '';
		if (region.includes('Wildlife') || region.includes('lowland')) {
			options = JOURNEY_SAFARI_OPTIONS;
		} else if (region.includes('Pilgrimage') || region.includes('culture') || region.includes('Kathmandu Valley')) {
			options = JOURNEY_HERITAGE_OPTIONS;
		} else {
			options = JOURNEY_TREK_OPTIONS;
		}
	}
	return options.filter((opt) => {
		if (opt.id === 'group') return isGroupBookingEnabled(journey);
		if (opt.id === 'private') return isPrivateBookingEnabled(journey);
		return true;
	});
}

function slugHaystack(destination) {
	return [
		destination.slug,
		destination.name,
		destination.district?.name,
		...(destination.tags || []).map((t) => t.slug),
	].filter(Boolean).join(' ').toLowerCase();
}

export function bookingOptionsForDestination(destination) {
	if (!destination) return [];

	const hay = slugHaystack(destination);
	const category = destination.category;

	// Everest / major treks
	if (
		hay.includes('everest')
		|| hay.includes('sagarmatha')
		|| hay.includes('annapurna-base-camp')
		|| hay.includes('base-camp')
		|| category === 'viewpoint'
		|| (destination.tags || []).some((t) => t.slug === 'trekking')
	) {
		return [
			{ id: 'group', title: 'Group Departure', desc: 'Fixed dates with other travelers', cta: 'View Departures', route: BOOKING_ROUTES.group },
			{ id: 'private', title: 'Private Trek', desc: 'Your dates, your group', cta: 'Request Private Tour', route: BOOKING_ROUTES.private },
		];
	}

	// Pokhara / lakes
	if (hay.includes('phewa') || hay.includes('pokhara') || category === 'lake') {
		return [
			{ id: 'private', title: 'Private Tour', desc: 'Lakeside, viewpoints, and day hikes on your schedule', cta: 'Plan Private Tour', route: BOOKING_ROUTES.private },
			{ id: 'shared', title: 'Shared Ride', desc: 'Kathmandu ↔ Pokhara and regional transfers', cta: 'Browse Shared Rides', route: BOOKING_ROUTES.shared },
		];
	}

	// Chitwan / safari parks
	if (hay.includes('chitwan') || (category === 'national_park' && !hay.includes('sagarmatha'))) {
		return [
			{ id: 'private', title: 'Safari Package', desc: 'Jungle safari arranged on your dates', cta: 'Request Safari Tour', route: BOOKING_ROUTES.private },
			{ id: 'private-alt', title: 'Private Tour', desc: 'Custom Terai itinerary beyond the park', cta: 'Plan Private Tour', route: BOOKING_ROUTES.private },
		];
	}

	// Kathmandu Valley heritage
	if (
		hay.includes('kathmandu')
		|| hay.includes('durbar')
		|| hay.includes('patan')
		|| hay.includes('bhaktapur')
		|| hay.includes('pashupatinath')
		|| hay.includes('boudhanath')
		|| hay.includes('swayambhunath')
		|| category === 'unesco_site'
		|| category === 'temple'
	) {
		return [
			{ id: 'private', title: 'Heritage Walking Tour', desc: 'Valley temples and squares with a cultural guide', cta: 'Request Private Tour', route: BOOKING_ROUTES.private },
			{ id: 'shared', title: 'Airport Transfer', desc: 'TIA to Thamel, Patan, or your hotel', cta: 'Browse Shared Rides', route: BOOKING_ROUTES.shared },
		];
	}

	// Lumbini / Janakpur pilgrimage
	if (hay.includes('lumbini') || hay.includes('janakpur') || hay.includes('muktinath')) {
		return [
			{ id: 'private', title: 'Private Pilgrimage Tour', desc: 'Your dates — VisitAllNepal handles logistics', cta: 'Request Private Tour', route: BOOKING_ROUTES.private },
			{ id: 'shared', title: 'Shared Ride', desc: 'Intercity transport to the Terai or hills', cta: 'Browse Shared Rides', route: BOOKING_ROUTES.shared },
		];
	}

	return [
		{ id: 'private', title: 'Private Tour', desc: 'Personalized itinerary for this region', cta: 'Plan Private Tour', route: BOOKING_ROUTES.private },
	];
}

export const TRAVEL_SERVICES_INTRO = {
	title: 'Book Nepal travel with your international card',
	lead: 'Group treks, homestays, private tours, shared rides & domestic flights — secure checkout in USD or NPR.',
};
